type JsonFallback<T> = T | null;

const STATE_SCHEMA_KEY = 'veeqWebPlayerStateSchema';
const INSTALLATION_ID_KEY = 'veeqWebPlayerInstallationId';
const CURRENT_SCHEMA_VERSION = 1;

const KNOWN_JSON_KEYS = [
  'announcements',
  'announcementMinuteLastPlayTimes',
  'processedSpecificAnnouncements',
  'currentPlaylist',
  'fallbackPlaylist',
  'schedules',
  'emergencySchedule',
  'deviceCertificate',
  'musicSchedule',
  'prayerTimes',
  'weeklyPrayerTimes',
  'prayerSettings',
  'specialDayConfig',
  'specialDayRuntime',
  'pendingPlaylistAutoplay',
  'veeq_playback_queue_history_v1',
  'veeq_enterprise_playback_history_v1'
];

function hasStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function createId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `wp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

export const localStateStore = {
  getString(key: string): string | null {
    if (!hasStorage()) return null;

    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },

  setString(key: string, value: string): boolean {
    if (!hasStorage()) return false;

    try {
      window.localStorage.setItem(key, value);
      return true;
    } catch {
      return false;
    }
  },

  remove(key: string): void {
    if (!hasStorage()) return;

    try {
      window.localStorage.removeItem(key);
    } catch {
      // ignore storage errors
    }
  },

  clear(): void {
    if (!hasStorage()) return;

    try {
      window.localStorage.clear();
    } catch {
      // ignore storage errors
    }
  },

  getJson<T>(key: string, fallback: JsonFallback<T> = null, removeOnError = true): JsonFallback<T> {
    const raw = this.getString(key);
    if (!raw) return fallback;

    try {
      return JSON.parse(raw) as T;
    } catch {
      if (removeOnError) {
        this.remove(key);
      }
      return fallback;
    }
  },

  setJson(key: string, value: unknown): boolean {
    try {
      return this.setString(key, JSON.stringify(value));
    } catch {
      return false;
    }
  }
};

export function getOrCreateInstallationId(): string {
  const existing = localStateStore.getString(INSTALLATION_ID_KEY);
  if (existing) return existing;

  const next = createId();
  localStateStore.setString(INSTALLATION_ID_KEY, next);
  return next;
}

export function ensureLocalStateSchema(): void {
  if (!hasStorage()) return;

  for (const key of KNOWN_JSON_KEYS) {
    const raw = localStateStore.getString(key);
    if (!raw) continue;

    try {
      JSON.parse(raw);
    } catch {
      localStateStore.remove(key);
    }
  }

  const currentVersion = Number(localStateStore.getString(STATE_SCHEMA_KEY) || '0');
  if (currentVersion !== CURRENT_SCHEMA_VERSION) {
    localStateStore.setString(STATE_SCHEMA_KEY, String(CURRENT_SCHEMA_VERSION));
  }

  getOrCreateInstallationId();
}

export const localStateKeys = {
  stateSchema: STATE_SCHEMA_KEY,
  installationId: INSTALLATION_ID_KEY
};
